import { PolicyInfoResponse } from 'all-in-one-shared-api/dist/apis/policy/PolicyInfoResponse';
import { DocumentsResponse, DocumentResponse } from 'all-in-one-shared-api/dist/apis/documents/DocumentsResponse';

import { VIEW_BENEFIT_SCHEDULE_ID } from 'pages/benefits/constants/variables';
import { BenefitAction, SelectionName, SelectionPeriod } from 'pages/benefits/types/benefitTypes';
import { CHINESE_SYMBOL } from 'i18n';
import { AmountBreakdowns, BenefitGroup, BenefitItems } from 'all-in-one-shared-api';
import DocumentIcon from 'assets/images/benefits/document-icon.svg';
import { formatDateString, isLocaleEnglish } from 'utils';

export const getPolicyByPolicyNumAndRenewalNumAndMemberType = (
  policyInfo: PolicyInfoResponse[],
  policyNumber: string,
  renewalNumber: string,
  memberType: string,
): PolicyInfoResponse | undefined => {
  const result = policyInfo.find(
    (policy) =>
      policy.policyNumber === policyNumber &&
      policy.renewalNumber === parseInt(renewalNumber) &&
      policy.memberType === memberType,
  );
  return result;
};

export const getPolicyYearFromBenefitSchedule = (
  benefitSchedule: DocumentResponse,
  policyInfo: PolicyInfoResponse[],
  memberType: string,
): { policyYear: string | undefined; yeartitle: string | undefined } => {
  if (!benefitSchedule.name) return { policyYear: undefined, yeartitle: undefined };
  // e.g. "CH_88000633_1_0.pdf"
  const policyDetails = benefitSchedule.name.replace('.pdf', '').split('_');
  const policyNumber = policyDetails[1];
  const renewalNumber = policyDetails[2];
  // const memberType = memberType;  // AIOBRB-2241 memberType deconstruction error

  const policy = getPolicyByPolicyNumAndRenewalNumAndMemberType(policyInfo, policyNumber, renewalNumber, memberType);

  if (!policy || !policy.startDate || !policy.endDate) return { policyYear: undefined, yeartitle: undefined };
  const yeartitle =
    formatDateString(policy.startDate, 'yyyy', 'DD/MM/YYYY') +
    '-' +
    formatDateString(policy.endDate, 'yyyy', 'DD/MM/YYYY');
  const policyYear = formatDateString(policy.startDate, 'yyyy', 'DD/MM/YYYY');
  return { policyYear, yeartitle };
};

export const updateActions = (
  DEFAULT_ACTIONS: BenefitAction[],
  memberType: string,
  documentsData?: DocumentsResponse,
  policyInfoData?: PolicyInfoResponse[],
): BenefitAction[] => {
  if (!documentsData || !policyInfoData) return [];

  const benefitSchedules = (documentsData && documentsData.benefitSchedule) ?? [];
  const hasBenefitScheduleDoc = benefitSchedules && benefitSchedules.length > 0;
  if (!hasBenefitScheduleDoc) return DEFAULT_ACTIONS;

  const actions = DEFAULT_ACTIONS;

  const benefitScheduleActions: BenefitAction[] = benefitSchedules
    .map((schedule) => {
      const yearobj = getPolicyYearFromBenefitSchedule(schedule, policyInfoData, memberType);
      const yeartitle = yearobj.yeartitle;
      const policyYear = yearobj.policyYear;
      return {
        id: `${VIEW_BENEFIT_SCHEDULE_ID}-${policyYear}`,
        title: 'benefits.viewBenefitSchedule',
        description: 'benefits.viewBenefitScheduleDescription',
        icon: DocumentIcon,
        to: schedule.url ?? '',
        isExternal: true,
        policyYear,
        yeartitle,
      };
    })
    .sort((a, b) => parseInt(b.policyYear ?? '') - parseInt(a.policyYear ?? ''));

  return actions.concat(benefitScheduleActions);
};

export const getCurrentPolicy = (
  PolicyInfoData?: PolicyInfoResponse[],
  selectedFilterName?: Partial<SelectionName>,
  selectedFilterPeriod?: Partial<SelectionPeriod>,
): PolicyInfoResponse | undefined => {
  if (!PolicyInfoData || !selectedFilterName || !selectedFilterPeriod) return;
  return PolicyInfoData.find((policy) => {
    return (
      policy.memberId === selectedFilterName.memberId &&
      policy.memberType === selectedFilterName.memberType &&
      policy.memberStartDate === selectedFilterPeriod.startDate &&
      policy.memberEndDate === selectedFilterPeriod.endDate
    );
  });
};

export const getRelationshipValue = (relationship: string) => {
  const relationShipValue = new Map<string, string>();
  relationShipValue.set('S', 'SPOUSE');
  relationShipValue.set('E', 'EMPLOYEE');
  relationShipValue.set('C', 'CHILD');
  return relationShipValue.get(relationship) ?? 'UNKNOWN';
};

export const isHasDependennts = (data: PolicyInfoResponse[] | undefined) => {
  return data ? data.filter((value) => value.memberType != '0').length > 0 : false;
};

export function getBreakdownLabel(i18n: string, breakdown: AmountBreakdowns) {
  return i18n == CHINESE_SYMBOL ? breakdown.nameCn : breakdown.name;
}

export const getCommonAccommodationLevel = (items: Array<BenefitItems>, language: string) => {
  // only include items whose accommodationLevel is not NULL/empty - ref: AIOBRB-839
  const notEmptyAccommondationLevelItems = items.filter((item) => item.accommodationLevel);

  const uniqueAcomondationLevel = new Set(notEmptyAccommondationLevelItems.map((item) => item.accommodationLevel));

  const firstAccommodationLevelItem = notEmptyAccommondationLevelItems[0];
  const accommodationLevelDesc = isLocaleEnglish(language)
    ? firstAccommodationLevelItem?.accommodationLevelDesc
    : firstAccommodationLevelItem?.accommodationLevelDescCn;
  return uniqueAcomondationLevel.size === 1 ? accommodationLevelDesc : undefined;
};

export const getCommonReimbursement = (items: Array<BenefitItems>) => {
  const isEqual = items.every((item) => item.reimbursementRate === items[0].reimbursementRate);
  return isEqual ? items[0]?.reimbursementRate : undefined;
};

export function getLimitLabel(i18n: string, benefit: BenefitItems) {
  return i18n == CHINESE_SYMBOL ? benefit.limitDescriptionCn : benefit.limitDescription;
}

export function getMaxAmountLabel(i18n: string, benefit: BenefitItems) {
  return i18n == CHINESE_SYMBOL ? benefit.maxAmountDescriptionCn : benefit.maxAmountDescription;
}

export function getNameLabel(i18n: string, benefit: BenefitItems) {
  return i18n == CHINESE_SYMBOL ? benefit.nameCn : benefit.name;
}

export function getRemarkLabel(i18n: string, benefit: BenefitItems) {
  return i18n == CHINESE_SYMBOL ? benefit.referralRemarkCn : benefit.referralRemark;
}

export function getGroupIdLabel(i18n: string, group: BenefitGroup) {
  return i18n == CHINESE_SYMBOL ? group.benefitGroupIdCn : group.benefitGroupId;
}

export function getGroupLimitDescriptionLabel(i18n: string, group: BenefitGroup) {
  if (group.limitDescription) {
    return i18n == CHINESE_SYMBOL ? group.limitDescriptionCn : group.limitDescription;
  }
  return '';
}

export function getGroupAmountDescriptionLabel(i18n: string, group: BenefitGroup) {
  if (group.amountDescription) {
    return i18n == CHINESE_SYMBOL ? group.amountDescriptionCn : group.amountDescription;
  }
  return '';
}

export function getAccommodationLevelDescription(i18n: string, benefit: BenefitItems) {
  return i18n == CHINESE_SYMBOL ? benefit.accommodationLevelDescCn : benefit.accommodationLevelDesc;
}
